import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Calendar, User, TrendingUp, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const EducationPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['All Topics', 'Insurance', 'Legal', 'Market Updates', 'Investment Tips', 'Retirement Planning', 'Tax Information'];

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === 'all'
    ? posts
    : posts.filter(p => p.category === selectedCategory);

  const featuredPosts = posts.filter(p => p.featured);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50" data-testid="education-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Thailand Property</span>
              </h1>
            </Link>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-full"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-12 px-6 lg:px-8" data-testid="education-hero">
        <div className="max-w-7xl mx-auto text-center">
          <p className="overline text-[#D4AF37] mb-4">{t('knowledge_hub')}</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6">
            {t('education_title')}
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto leading-relaxed">
            {t('education_desc')}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 lg:px-8 mb-12" data-testid="category-filter">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 justify-center">
          {categories.map((cat) => {
            const catVal = cat === 'All Topics' ? 'all' : cat;
            const isActive = selectedCategory === catVal;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(catVal)}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-colors duration-300 ${
                  isActive
                    ? 'bg-[#D4AF37] text-[#0A0E27]'
                    : 'bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-white border border-white/5'
                }`}
                data-testid={`category-${catVal}`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && selectedCategory === 'all' && (
        <section className="px-6 lg:px-8 mb-16" data-testid="featured-posts">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="overline text-[#D4AF37] mb-2">{t('featured')}</p>
                <h3 className="font-heading text-3xl font-semibold text-white">{t('featured_articles')}</h3>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPosts.slice(0, 2).map((post) => (
                <ArticleCard key={post.id} post={post} navigate={navigate} formatDate={formatDate} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="px-6 lg:px-8 pb-24" data-testid="all-posts">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-heading text-3xl font-semibold text-white mb-8">
            {selectedCategory === 'all' ? 'All Articles' : selectedCategory}
          </h3>

          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-[#D4AF37] border-r-transparent" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20 bg-[#131A38] rounded-2xl border border-white/5" data-testid="no-posts-message">
              <BookOpen className="w-12 h-12 mx-auto text-[#D4AF37]/40 mb-4" />
              <p className="text-[#94A3B8]">No articles available in this category yet.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <ArticleCard key={post.id} post={post} navigate={navigate} formatDate={formatDate} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const ArticleCard = ({ post, navigate, formatDate, featured }) => (
  <div
    className={`property-card bg-[#131A38] rounded-2xl overflow-hidden border cursor-pointer group ${
      featured ? 'border-[#D4AF37]/20 hover:border-[#D4AF37]/50' : 'border-white/5 hover:border-[#D4AF37]/30'
    }`}
    onClick={() => navigate(`/education/${post.id}`)}
    data-testid={`article-card-${post.id}`}
  >
    {post.image_url && (
      <div className="h-48 overflow-hidden bg-[#0A0E27]">
        <img src={post.image_url} alt={post.title} className="property-image w-full h-full object-cover" />
      </div>
    )}
    <div className="p-6">
      <div className="flex items-center gap-3 mb-3">
        <span className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] font-semibold rounded-full uppercase tracking-wider">
          {post.category}
        </span>
        <span className="text-[#475569] text-xs flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(post.created_at)}
        </span>
      </div>
      <h4 className="font-heading text-xl font-semibold text-white mb-2 line-clamp-2 group-hover:text-[#D4AF37] transition-colors duration-300">
        {post.title}
      </h4>
      <p className="text-[#94A3B8] text-sm line-clamp-3 mb-4 leading-relaxed">{post.excerpt}</p>
      <div className="flex items-center justify-between pt-4 border-t border-white/5">
        <div className="flex items-center gap-2 text-[#94A3B8] text-xs">
          <User className="w-3.5 h-3.5" />
          <span>{post.author}</span>
        </div>
        <span className="text-[#D4AF37] text-xs font-medium flex items-center gap-1 group-hover:translate-x-1 transition-transform duration-300">
          Read <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  </div>
);

export default EducationPage;
